/**
 * Verona Tempo — product photos lined up (Sep 2026). Every product photo on the cards (collections, search, homepage
 * rows, "You may also like", Last pairs, wishlist) shows the product at the same size, centred, standing on the same
 * line. The photo files are never changed: each photo is read small (96 px) on a canvas, the product is found
 * (pixels that differ from the photo's edge colour), and the image is scaled and moved with CSS.
 *  - Covers every place a product photo is shown (list in SEL): cards, recently viewed, wishlist, top 3, Top 10,
 *    season band, quick add, "added to bag", the bag, compare, upsell, and the product page gallery + its thumbnails
 *    (Sep 2026; the zoom view still opens the photo as shot). Not banners. Full scenes (edge to edge) are left as shot.
 *  - Photos served from cdn.shopify.com (another address) are read through a second, CORS copy of the same file.
 *  - The backdrop fades out from the photo's edge to just around the product (a per-photo CSS mask), and a light
 *    grey studio backdrop is also brightened (CSS brightness, at most +18%), so every tile reads as plain white.
 *  - When a product is enlarged, the browser is asked for a bigger file (img.sizes), so it stays sharp.
 *  - Works on every photo as it loads (a capturing "load" listener), so infinite scroll, quick view, colour swatches
 *    and new products need nothing else. If a photo cannot be read it simply shows as shot.
 * Styles: assets/vt-r14.css "3b". Switch: Theme settings > Verona Tempo > "Line up product photos" (replaces the
 * round-14 "Even product size on cards" zoom wherever it runs).
 */
(function () {
  'use strict';
  if (window.vtLine) return;
  window.vtLine = true;
  var SEL = '.product-block .image-cont img.theme-img, .vt-card__media img, .vt11-sw__slide img, .vt-lps__media img, img.vt-sc__img, img.vt-r12-top3__img, img.vt-r12-dk__img, img.vt-season__card-img, img.vt-edit__img, img.vt-qa__img, img.vt-r10-recent__img, .vt-r12-best__media img, img.vt-r12-added__img, img.vt-cmp__img, img.vt-menu-recent__img, .vt-upsell__media img, .cart-item__image img, img.vt-cstrip__img, .product-media--image img.theme-img, .thumbnail--media-image img.theme-img';
  var root = document.documentElement;
  root.classList.add('vt-line');

  function lum(p) { return (p[0] + p[1] + p[2]) / 3; }

  function fit(img) {
    try { measure(img, img); }
    catch (err) {
      // another address (cdn.shopify.com): the canvas may not read it directly; read a CORS copy of the same file
      if (img.__vtLineProbe) { img.classList.add('is-lined'); return; }
      var probe = new Image();
      img.__vtLineProbe = probe;
      probe.crossOrigin = 'anonymous';
      probe.onload = function () { try { measure(img, probe); } catch (e2) { /* shows as shot */ } img.classList.add('is-lined'); };
      probe.onerror = function () { img.classList.add('is-lined'); };
      probe.src = img.currentSrc || img.src;
      return;
    }
    // 2 Nov 2026: a tile with no size yet (closed drawer, hidden slide) is not marked lined, so it still waits unseen and
    // is lined when it shows (ResizeObserver below) instead of showing as shot and jumping then
    if (img.__vtZero) return;
    img.classList.add('is-lined');
  }

  function measure(img, src) {
    var done = function () {};
    {
      var nw = img.naturalWidth, nh = img.naturalHeight;
      var bw = img.clientWidth, bh = img.clientHeight;
      img.__vtZero = !!(nw && nh && (!bw || !bh));
      if (!nw || !nh || !bw || !bh) return done();
      var S = 96;
      var cw = Math.max(8, Math.round(nw >= nh ? S : S * nw / nh));
      var ch = Math.max(8, Math.round(nh >= nw ? S : S * nh / nw));
      var cv = document.createElement('canvas'); cv.width = cw; cv.height = ch;
      var cx = cv.getContext('2d', { willReadFrequently: true });
      cx.fillStyle = '#fff'; cx.fillRect(0, 0, cw, ch); cx.drawImage(src, 0, 0, cw, ch);
      var d = cx.getImageData(0, 0, cw, ch).data;
      var px = function (x, y) { var i = (y * cw + x) * 4; return [d[i], d[i + 1], d[i + 2]]; };
      // the backdrop: the photo's outer edge
      var ring = [];
      for (var e = 0; e < cw; e += 2) ring.push(px(e, 0), px(e, ch - 1));
      for (var f = 0; f < ch; f += 2) ring.push(px(0, f), px(cw - 1, f));
      var bg = [0, 1, 2].map(function (c) { var v = ring.map(function (p) { return p[c]; }).sort(function (a, b) { return a - b; }); return v[v.length >> 1]; });
      var lums = ring.map(lum).sort(function (a, b) { return a - b; });
      var low = lums[Math.floor(lums.length * 0.15)];
      // where the product is
      // two outlines in one pass: a firm one (sizes and places the product) and a loose one that also catches the
      // pale parts of a white or cream product on a white backdrop (the fade never starts inside it)
      var hiL = lums[Math.floor(lums.length * 0.95)] + 4;
      var x0 = cw, y0 = ch, x1 = -1, y1 = -1, q0 = cw, r0 = ch, q1 = -1, r1 = -1, u0 = cw, v0 = ch, u1 = -1, v1 = -1, lightN = 0;
      var mk = new Uint8Array(cw * ch);
      for (var y = 0; y < ch; y++) {
        var hits = 0, fx = -1, lx = -1, soft = 0, gx = -1, hx = -1, lit = 0, ax = -1, bx = -1;
        for (var x = 0; x < cw; x++) {
          var i = (y * cw + x) * 4;
          var dv = Math.max(Math.abs(d[i] - bg[0]), Math.abs(d[i + 1] - bg[1]), Math.abs(d[i + 2] - bg[2]));
          if (dv > 34) { hits++; mk[y * cw + x] = 1; if (fx < 0) fx = x; lx = x; }
          if (dv > 12) { soft++; if (gx < 0) gx = x; hx = x; }
          if (hiL < 250 && (d[i] + d[i + 1] + d[i + 2]) / 3 > hiL) { lit++; if (ax < 0) ax = x; bx = x; }
        }
        if (hits >= 2) { if (fx < x0) x0 = fx; if (lx > x1) x1 = lx; if (y < y0) y0 = y; y1 = y; }
        if (soft >= 3) { if (gx < q0) q0 = gx; if (hx > q1) q1 = hx; if (y < r0) r0 = y; r1 = y; }
        if (lit >= 2) { lightN += lit; if (ax < u0) u0 = ax; if (bx > u1) u1 = bx; if (y < v0) v0 = y; v1 = y; }
      }
      // nothing found, or the photo is a full scene (the product fills it edge to edge): leave it as shot
      if (x1 < 0 || x1 - x0 < 3 || y1 - y0 < 3 || ((x1 - x0) > cw * 0.97 && (y1 - y0) > ch * 0.97)) return done();
      // in tile widths: the tile is 1 wide and H tall; the photo is drawn whole (contain) inside it
      var H = bh / bw, a = nw / nh, dw, dh, ox, oy;
      if (a >= 1 / H) { dw = 1; dh = 1 / a; ox = 0; oy = (H - dh) / 2; } else { dh = H; dw = H * a; oy = 0; ox = (1 - dw) / 2; }
      var sx0 = ox + x0 / cw * dw, sx1 = ox + (x1 + 1) / cw * dw, sy0 = oy + y0 / ch * dh, sy1 = oy + (y1 + 1) / ch * dh;
      // the size and the line come from the product itself: a loose piece lying apart (a bag's shoulder strap on the
      // floor, a tag) is left out, so a bag shot with its strap is as big as one shot without
      var mb = mainBox(mk, cw, ch) || [x0, y0, x1, y1];
      // bags (Sep 2026, user: "make the bags line up too like shoes"): a strap or wrist loop lying close to the bag was
      // taken as part of it, so the strap stood on the line and the bag floated above it, smaller; tall handles made
      // handbags narrow. For bags the BODY is found (thin straps and handles peeled away) and that is sized and stood
      // on the line; straps may hang below and handles rise above, inside the tile.
      var bag = !!(img.closest('[data-vt-kind="bag"]') || (root.getAttribute('data-vt-kind') === 'bag' && img.closest('.product-media--image, .thumbnail--media-image') && !img.closest('product-block')));
      var body = bag ? bodyBox(mk, cw, ch) : null;
      if (body) mb = body;
      var bx0 = ox + mb[0] / cw * dw, bx1 = ox + (mb[2] + 1) / cw * dw, by0 = oy + mb[1] / ch * dh, by1 = oy + (mb[3] + 1) / ch * dh;
      // the same line for every product (0.779 of the tile, where it has always been); tall things (bags, boots) may
      // use a little more height so they are not smaller than the shoes
      var maxW = 0.8, maxH = H * 0.68, base = H * 0.779, cap = 1.8, floor = 0.96;
      // the product page's big photos on a phone: one photo at a time, so the product fills most of the frame
      // (owner, Sep 2026: "make the product page photos bigger on phone"); the thumbnails keep the normal size
      if (img.closest('.product-media--image') && window.matchMedia('(max-width: 767.98px)').matches) { maxW = 0.94; maxH = H * 0.82; base = H * 0.88; cap = 2.4; floor = 0.91; }
      if (body) { maxW = Math.min(maxW, 0.7); maxH = Math.min(maxH, H * 0.5); }
      var s = Math.min(maxW / (bx1 - bx0), maxH / (by1 - by0), cap);
      // a bag's handle must fit above the line without pushing the bag off it
      if (body) s = Math.min(s, (base - H * 0.04) / Math.max(0.01, by1 - sy0));
      // the loose pieces must still fit inside the tile: a strap lying in front may hang below the line, down to the
      // tile's bottom edge; only if it would pass that does the product move up a little (it is never made smaller)
      s = Math.min(s, 0.94 / (sx1 - sx0), H * (floor - 0.06) / (sy1 - sy0));
      var tx = 0.5 - s * (bx0 + bx1) / 2, ty = base - s * by1;
      tx = Math.min(Math.max(tx, 0.03 - s * sx0), 0.97 - s * sx1);
      if (ty + s * sy1 > H * floor) ty = H * floor - s * sy1;
      if (ty + s * sy0 < H * 0.03) ty = H * 0.03 - s * sy0;
      // the fade: see-through at the photo's edge, solid from just outside the product (in % of the tile, before the move)
      var pc = function (v) { return (v * 100).toFixed(2) + '%'; };
      // a white or cream product on a grey backdrop: its white parts are lighter than the backdrop, so lifting the grey
      // all the way to white would wash them out. Count them; if there are many, lift only part way and fade from a
      // wider margin around the firm outline (the loose one would take in the whole grey square).
      var bgL = lum(bg), light = 0, area = 0;
      for (var yy = y0; yy <= y1; yy++) for (var xx = x0; xx <= x1; xx++) { area++; if (lum(px(xx, yy)) > bgL + 6) light++; }
      var pale = light >= area * 0.04 || lightN >= cw * ch * 0.01;
      // Sep 2026 (user: "line them up but not eye-blinding"): a light-toned product (camel, beige, nude, cream — COSY1)
      // on a grey backdrop was washed out by the full lift. Its average tone is measured on the firm outline; a light
      // one gets only a gentle lift and no extra contrast. Darker products keep the full lift, as before.
      var pL = 0, pN = 0, sL = 0, sN = 0;
      for (var yq = y0; yq <= y1; yq++) for (var xq = x0; xq <= x1; xq++) {
        if (mk[yq * cw + xq]) { pL += lum(px(xq, yq)); pN++; continue; }
        // the faint parts (a little off the backdrop, not enough for the firm outline): a white body on grey is here
        var pq = px(xq, yq), dq = Math.max(Math.abs(pq[0] - bg[0]), Math.abs(pq[1] - bg[1]), Math.abs(pq[2] - bg[2]));
        if (dq > 12) { sL += lum(pq); sN++; }
      }
      var lightTone = pN > 0 && pL / pN > 150;
      var faintBright = sN > (x1 - x0 + 1) * (y1 - y0 + 1) * 0.08 && sL / sN >= bgL - 8;
      img.style.removeProperty('--vt-lb'); img.style.removeProperty('--vt-lc'); img.style.removeProperty('filter');
      if (low > 185 && low < 247) {
        // light-toned product: only the backdrop's own shade is taken to white (a per-colour "levels" SVG filter —
        // SHADO, MARO on blue-grey), tones clearly darker than the backdrop (the product, its shadow) stay as shot
        // ...only when the product is clearly darker than the backdrop; a white piece on grey (SHADO, white sneakers)
        // would vanish into the white, so it keeps the gentle lift
        if (!pale && lightTone && bgL >= 225 && pL / pN < bgL - 22 && !faintBright) img.style.setProperty('filter', 'url(#' + levels(bg) + ')', 'important');
        else if (!pale && lightTone) img.style.setProperty('--vt-lb', Math.min(1.05, 250 / low).toFixed(3));
        else if (!pale) { img.style.setProperty('--vt-lb', Math.min(1.18, 259 / low).toFixed(3)); img.style.setProperty('--vt-lc', '1.07'); }
        else if (low < 226) img.style.setProperty('--vt-lb', Math.min(1.1, 236 / low).toFixed(3));
      }
      var L = ox, R = ox + dw, Tp = oy / H, B = (oy + dh) / H, m = pale ? 0.09 : 0.05, e0 = 0.03;
      if (q1 < 0 || pale) { q0 = x0; q1 = x1; r0 = y0; r1 = y1; }
      // pale parts brighter than the backdrop belong to the product: the fade starts outside them too
      if (pale && u1 >= 0) { q0 = Math.min(q0, u0); q1 = Math.max(q1, u1); r0 = Math.min(r0, v0); r1 = Math.max(r1, v1); }
      var fx0 = Math.min(sx0, ox + q0 / cw * dw), fx1 = Math.max(sx1, ox + (q1 + 1) / cw * dw);
      var fy0 = Math.min(sy0, oy + r0 / ch * dh), fy1 = Math.max(sy1, oy + (r1 + 1) / ch * dh);
      img.style.setProperty('--vt-ma', pc(L));
      // always at least a thin fade right at the photo's edge, so no edge line shows
      img.style.setProperty('--vt-mb', pc(Math.max(L + e0, fx0 - m)));
      img.style.setProperty('--vt-mc', pc(Math.min(R - e0, fx1 + m)));
      img.style.setProperty('--vt-md', pc(R));
      img.style.setProperty('--vt-me', pc(Tp));
      img.style.setProperty('--vt-mf', pc(Math.max(Tp + e0 / H, fy0 / H - m / H)));
      img.style.setProperty('--vt-mg', pc(Math.min(B - e0 / H, fy1 / H + m / H)));
      img.style.setProperty('--vt-mh', pc(B));
      img.classList.add('vt-fitted');
      img.style.setProperty('--vt-ls', s.toFixed(4));
      img.style.setProperty('--vt-lx', (tx * 100).toFixed(2) + '%');
      img.style.setProperty('--vt-ly', (ty / H * 100).toFixed(2) + '%');

      // enlarged: ask for a file big enough for the drawn size (the browser picks it from srcset, then this runs again)
      // 12 Oct 2026 (owner: "pictures take long on the phone"): never more than a 2x-sharp file (3x phones were
      // downloading 2000-2400 px photos for a 390 px box), and no bigger file at all on data saver
      var saveData = navigator.connection && navigator.connection.saveData;
      if (s > 1.08 && img.srcset && !saveData) {
        var want = Math.ceil(bw * s * Math.min(1, 2 / (window.devicePixelRatio || 1))) + 'px';
        if (img.getAttribute('sizes') !== want) { img.dataset.vtSizes = img.getAttribute('sizes') || ''; img.setAttribute('sizes', want); }
      }
    }
  }

  // the box around the product's main parts on the small canvas: pieces at least a fifth the size of the biggest one
  // (both shoes of a pair, both earrings) stay, small loose ones go. null = use everything.
  function mainBox(m, w, h) {
    var g = new Uint8Array(w * h), lab = new Int32Array(w * h), comps = [], total = 0, x, y, i;
    // each hit grows by one pixel, so a thin strap broken up at this small size stays one piece
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) {
      if (!m[y * w + x]) continue;
      total++;
      for (var dy = -1; dy <= 1; dy++) for (var dx = -1; dx <= 1; dx++) {
        var yy = y + dy, xx = x + dx;
        if (yy >= 0 && yy < h && xx >= 0 && xx < w) g[yy * w + xx] = 1;
      }
    }
    for (i = 0; i < w * h; i++) {
      if (!g[i] || lab[i]) continue;
      var c = { n: 0, x0: w, y0: h, x1: -1, y1: -1 }, stack = [i];
      lab[i] = comps.length + 1;
      while (stack.length) {
        var p = stack.pop(), px = p % w, py = (p - px) / w;
        if (m[p]) { c.n++; if (px < c.x0) c.x0 = px; if (px > c.x1) c.x1 = px; if (py < c.y0) c.y0 = py; if (py > c.y1) c.y1 = py; }
        for (var ny = py - 1; ny <= py + 1; ny++) for (var nx = px - 1; nx <= px + 1; nx++) {
          if (ny < 0 || ny >= h || nx < 0 || nx >= w) continue;
          var q = ny * w + nx;
          if (g[q] && !lab[q]) { lab[q] = comps.length + 1; stack.push(q); }
        }
      }
      if (c.n) comps.push(c);
    }
    if (comps.length < 2) return null;
    var big = 0, kept = 0, b = [w, h, -1, -1];
    comps.forEach(function (c) { if (c.n > big) big = c.n; });
    comps.forEach(function (c) {
      if (c.n < big * 0.2) return;
      kept += c.n;
      b[0] = Math.min(b[0], c.x0); b[1] = Math.min(b[1], c.y0); b[2] = Math.max(b[2], c.x1); b[3] = Math.max(b[3], c.y1);
    });
    // a pale product found only in bits (a white bag on white): too little is left, so use everything
    if (kept < total * 0.6 || b[2] - b[0] < 3 || b[3] - b[1] < 3) return null;
    return b;
  }

  // one SVG filter per backdrop brightness (rounded, so photos with the same backdrop share it). It lays plain white
  // over the photo, only where a pixel is about as bright as the backdrop or brighter: none below "backdrop − 30",
  // full at the backdrop's brightness. Plain white is added (no colour channel is shifted on its own), so a white
  // product on a blue-grey backdrop (SHADO) gets no tint; darker tones — the product, its shadow — stay as shot.
  var lvlSvg = null, lvlMade = {};
  function levels(bg) {
    var L = Math.round((0.2126 * bg[0] + 0.7152 * bg[1] + 0.0722 * bg[2]) / 4) * 4;
    var id = 'vt-lvl-' + L;
    if (lvlMade[id]) return id;
    if (!lvlSvg) {
      lvlSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      lvlSvg.setAttribute('width', '0'); lvlSvg.setAttribute('height', '0'); lvlSvg.setAttribute('aria-hidden', 'true');
      lvlSvg.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
      (document.body || root).appendChild(lvlSvg);
    }
    // how much white, by brightness: 0 up to L − 34, rising to 1 at L − 4
    var end = L - 4, t0 = end - 30, ramp = [];
    for (var i = 0; i <= 32; i++) { var x = i * 255 / 32; ramp.push((x <= t0 ? 0 : x >= end ? 1 : (x - t0) / (end - t0)).toFixed(4)); }
    var NS = 'http://www.w3.org/2000/svg', mk = function (tag, attrs) { var e = document.createElementNS(NS, tag); for (var a in attrs) e.setAttribute(a, attrs[a]); return e; };
    var f = mk('filter', { id: id, 'color-interpolation-filters': 'sRGB', x: '0', y: '0', width: '1', height: '1' });
    f.appendChild(mk('feColorMatrix', { in: 'SourceGraphic', type: 'matrix', values: '0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.2126 0.7152 0.0722 0 0', result: 'lum' }));
    var ct = mk('feComponentTransfer', { in: 'lum', result: 'amt' }); ct.appendChild(mk('feFuncA', { type: 'table', tableValues: ramp.join(' ') })); f.appendChild(ct);
    f.appendChild(mk('feFlood', { 'flood-color': '#ffffff', 'flood-opacity': '1', result: 'white' }));
    f.appendChild(mk('feComposite', { in: 'white', in2: 'amt', operator: 'in', result: 'veil' }));
    f.appendChild(mk('feComposite', { in: 'veil', in2: 'SourceGraphic', operator: 'over' }));
    lvlSvg.appendChild(f);
    lvlMade[id] = true;
    return id;
  }

  // a bag's body on the small canvas: peel two pixels off every edge (straps, handles and chains are only a pixel or
  // two wide at this size, so they vanish), keep the big pieces, then add the two pixels back. null = no clear body.
  function bodyBox(m, w, h) {
    var a = m, b, pass, x, y, total = 0, left = 0;
    for (var t = 0; t < w * h; t++) total += m[t];
    for (pass = 0; pass < 2; pass++) {
      b = new Uint8Array(w * h);
      for (y = 1; y < h - 1; y++) for (x = 1; x < w - 1; x++) {
        var i = y * w + x;
        if (a[i] && a[i - 1] && a[i + 1] && a[i - w] && a[i + w] && a[i - w - 1] && a[i - w + 1] && a[i + w - 1] && a[i + w + 1]) b[i] = 1;
      }
      a = b;
    }
    for (t = 0; t < w * h; t++) left += a[t];
    if (!total || left < total * 0.2) return null;
    var bb = mainBox(a, w, h);
    if (!bb) {
      bb = [w, h, -1, -1];
      for (y = 0; y < h; y++) for (x = 0; x < w; x++) if (a[y * w + x]) { if (x < bb[0]) bb[0] = x; if (x > bb[2]) bb[2] = x; if (y < bb[1]) bb[1] = y; if (y > bb[3]) bb[3] = y; }
      if (bb[2] < 0) return null;
    }
    return [Math.max(0, bb[0] - 2), Math.max(0, bb[1] - 2), Math.min(w - 1, bb[2] + 2), Math.min(h - 1, bb[3] + 2)];
  }

  // 7 Oct 2026 (speed on mid-range Android): photos are lined up only when they come near the screen, a few at a
  // time (at most ~12 ms of work per step), so the page never freezes while dozens of cards load. A photo far below
  // the screen waits until she scrolls towards it; the ones on screen go first.
  var near = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) { if (en.isIntersecting) { near.unobserve(en.target); queue(en.target); } });
  }, { rootMargin: '400px 100%' }) : null;
  var q = [], draining = false;
  function queue(img) {
    if (q.indexOf(img) < 0) q.push(img);
    if (draining) return;
    draining = true;
    (window.requestAnimationFrame || window.setTimeout)(drain);
  }
  function drain() {
    var t0 = Date.now();
    // 2 Nov 2026: photos on screen first (a bags page queues ~4 photos per card at once)
    var vh = window.innerHeight, on = [], off = [];
    q.forEach(function (im) { var r = im.getBoundingClientRect(); (r.bottom > 0 && r.top < vh ? on : off).push(im); });
    q = on.concat(off);
    while (q.length && Date.now() - t0 < 12) {
      var img = q.shift();
      if (img.isConnected && img.complete && img.naturalWidth) fit(img);
    }
    if (q.length) window.setTimeout(drain, 0); else draining = false;
  }
  function maybe(img) {
    if (!img || img.tagName !== 'IMG' || !img.matches(SEL)) return;
    if (!(img.complete && img.naturalWidth)) return;
    if (img.__vtLineSrc !== img.currentSrc) {
      var was = img.__vtLineSrc;
      img.__vtLineSrc = img.currentSrc; img.__vtLineProbe = null;
      // 2 Nov 2026 (owner: "items glitching"): the same photo in a bigger file (asked for above when enlarged) is not
      // measured again — that nudged it a pixel or two; a different photo (colour dot) waits unseen and fades in lined
      if (was && img.classList.contains('is-lined')) {
        if (picOf(was) === picOf(img.currentSrc)) return;
        img.classList.remove('is-lined', 'vt-lw-ok');
      }
    }
    if (near) near.observe(img); else fit(img);
  }
  // the same photo in another width: Shopify's ?width= / &width= (and crop/height) left out
  function picOf(u) { return String(u || '').replace(/([?&])(width|height|crop)=[^&]*&?/g, '$1').replace(/[?&]$/, ''); }
  // 2 Nov 2026: a photo waits unseen until it is lined up (assets/vt-r14.css "3c"); one that has been on screen, loaded,
  // for 0.7 s and is still not lined is lined there and then, or shows as shot (.vt-lw-ok), so nothing ever stays blank
  function guard(img) {
    if (img.__vtG || img.classList.contains('is-lined')) return;
    img.__vtG = window.setTimeout(function () {
      img.__vtG = 0;
      if (img.classList.contains('is-lined') || !(img.complete && img.naturalWidth)) return;
      var r = img.getBoundingClientRect();
      if (!(r.width && r.bottom > 0 && r.top < window.innerHeight)) return;
      // the queue is busy (many cards arrived at once): line this one up now, out of turn; only if that cannot be done
      // does it show as shot
      img.__vtLineSrc = img.currentSrc;
      try { fit(img); } catch (e) { /* shows as shot */ }
      if (!img.classList.contains('is-lined') && !img.__vtLineProbe) img.classList.add('vt-lw-ok');
    }, 700);
  }
  var seen = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (en) { if (en.isIntersecting) guard(en.target); });
  }) : null;
  // 2 Nov 2026: a sweep twice a second lines up any photo that has arrived, is on or within a screen of the view and
  // was missed by the queue (fast scrolling, cards added by "load more"), so none sits blank waiting for the guard
  window.setInterval(function () {
    if (document.hidden) return;
    var vh = window.innerHeight, n = 0;
    [].forEach.call(document.querySelectorAll(SEL), function (img) {
      if (n > 24 || img.classList.contains('is-lined') || img.classList.contains('vt-lw-ok') || img.__vtLineProbe) return;
      if (!(img.complete && img.naturalWidth) || !img.clientWidth) return;
      var r = img.getBoundingClientRect();
      if (r.bottom < -vh || r.top > 2 * vh) return;
      n++; img.__vtLineSrc = img.currentSrc;
      try { fit(img); } catch (e) { /* the guard shows it as shot */ }
    });
  }, 500);
  function watchSeen(img) { if (img.__vtSeen) return; img.__vtSeen = true; if (seen) seen.observe(img); else img.classList.add('vt-lw-ok'); }
  document.addEventListener('load', function (e) {
    var t = e.target;
    if (t && t.tagName === 'IMG' && t.matches(SEL)) {
      watchSeen(t);
      var r = t.getBoundingClientRect();
      if (r.bottom > 0 && r.top < window.innerHeight) guard(t);
    }
    maybe(t);
  }, true);
  document.addEventListener('error', function (e) { if (e.target && e.target.tagName === 'IMG' && e.target.matches(SEL)) e.target.classList.add('is-lined'); }, true);
  var start = function () { [].forEach.call(document.querySelectorAll(SEL), function (img) { watchSeen(img); maybe(img); }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
  // a tile that was hidden (size 0) when its photo loaded — e.g. a closed drawer or a tab — is fitted when it shows
  if (window.ResizeObserver) {
    var ro = new ResizeObserver(function (list) { list.forEach(function (en) {
      if (en.contentRect.width > 0 && !en.target.style.getPropertyValue('--vt-ls')) {
        // it had no size when it arrived: it waits unseen until lined now that it shows
        if (en.target.__vtZero) { en.target.classList.remove('vt-lw-ok'); en.target.__vtLineSrc = null; }
        maybe(en.target);
      }
    }); });
    var watch = function () { [].forEach.call(document.querySelectorAll(SEL), function (img) { watchSeen(img); if (!img.__vtLineWatch) { img.__vtLineWatch = true; ro.observe(img); } }); };
    watch();
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.setTimeout(function () { queued = false; watch(); }, 250);
    }).observe(document.body || root, { childList: true, subtree: true });
  }
})();
